import React, { useState, useRef, useCallback } from 'react';
import { X, Loader, Camera, Check, MessageCircle } from 'lucide-react';
import Cropper, { Area } from 'react-easy-crop';
import { readArtistToken, updateArtistDisplayName, uploadArtistAvatar, fetchProfileFromWhatsApp } from '../lib/supabase';
import { useParams } from 'react-router-dom';

interface ArtistSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Crop the image to the given pixel area and compress it.
 * Returns a JPEG Blob (max 512x512, quality 0.85).
 */
async function cropAndCompress(imageSrc: string, pixelCrop: Area): Promise<File> {
  const img = new Image();
  img.src = imageSrc;
  await new Promise((resolve) => { img.onload = resolve; });

  const canvas = document.createElement('canvas');
  const maxSize = 512;
  canvas.width = maxSize;
  canvas.height = maxSize;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(
    img,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    maxSize,
    maxSize
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(new File([blob!], 'avatar.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.85
    );
  });
}

export const ArtistSettingsModal: React.FC<ArtistSettingsModalProps> = ({ isOpen, onClose }) => {
  const { id: artistId } = useParams<{ id: string }>();
  const token = readArtistToken();
  const [displayName, setDisplayName] = useState(token?.display_name ?? '');
  const [avatarUrl, setAvatarUrl] = useState(token?.avatar_url ?? '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingWa, setFetchingWa] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  if (!isOpen) return null;

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0 || !parts[0]) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(displayName || token?.display_name || '');

  const handleSaveName = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await updateArtistDisplayName(displayName.trim());
      setSuccess(true);
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save display name');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    // Read file as data URL for the cropper
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedAreaPixels) return;

    setUploading(true);
    setError('');
    try {
      const compressedFile = await cropAndCompress(cropSrc, croppedAreaPixels);
      const url = await uploadArtistAvatar(compressedFile);
      setAvatarUrl(url);
      setCropSrc(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleFetchFromWhatsApp = async () => {
    if (!artistId) return;
    setFetchingWa(true);
    setError('');
    setSuccess(false);
    try {
      const result = await fetchProfileFromWhatsApp(artistId);
      if (result.display_name) setDisplayName(result.display_name);
      if (result.avatar_url) setAvatarUrl(result.avatar_url);
      if (!result.display_name && !result.avatar_url) {
        setError('No profile name or photo found on WhatsApp');
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch from WhatsApp');
    } finally {
      setFetchingWa(false);
    }
  };

  // Crop mode overlay
  if (cropSrc) {
    return (
      <div className="fixed inset-0 z-[101] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Crop Image</h2>
            <button
              onClick={handleCropCancel}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cropper */}
          <div className="relative w-full h-80 bg-gray-900">
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="round"
              showGrid={false}
            />
          </div>

          {/* Zoom slider + confirm */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-primary-500"
              />
            </div>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={handleCropCancel}
                disabled={uploading}
                className="flex-1 py-2.5 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={uploading}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {uploading ? (
                  <><Loader className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <div className="relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary-500/10 flex items-center justify-center border-4 border-gray-200 dark:border-gray-700">
                  <span className="text-3xl font-bold text-primary-500">{initials}</span>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary-500 hover:bg-primary-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Click camera to upload profile picture
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                maxLength={50}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                onClick={handleSaveName}
                disabled={!displayName.trim() || saving}
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : success ? (
                  <Check className="w-4 h-4" />
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}

          {/* Phone (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone
            </label>
            <input
              type="text"
              value={token?.phone ?? ''}
              disabled
              className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed"
            />
          </div>

          {/* Fetch from WhatsApp */}
          <button
            onClick={handleFetchFromWhatsApp}
            disabled={fetchingWa}
            className="w-full py-2.5 bg-green-500/10 hover:bg-green-500/20 disabled:opacity-50 text-green-600 dark:text-green-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {fetchingWa ? (
              <><Loader className="w-4 h-4 animate-spin" /> Fetching from WhatsApp...</>
            ) : (
              <><MessageCircle className="w-4 h-4" /> Fetch profile from WhatsApp</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
